/**
 * Turning an FFT frame into bar heights. No React, no WebAudio, no DOM.
 *
 * Its own file because it is the only part of the meter that can be wrong in a
 * way you cannot see: a band table off by a few bins still draws something that
 * moves when somebody talks, and looks fine right up until you notice the bars
 * on the right never leave the floor. Pure input to pure output, so it can be
 * checked against numbers instead of against a screenshot.
 */

/**
 * The band the meter looks at.
 *
 * A phone line is not hi-fi: the codec on the far end passes roughly 300 Hz to
 * 3400 Hz and throws away everything else, so a meter spread evenly across
 * 0-24 kHz would be one twitching bar on the left and forty dead ones beside it.
 * The floor sits under the passband to catch the operator's own mic, which is
 * NOT band-limited, and the ceiling just above it so consonants have somewhere
 * to land.
 */
export const WAVE_MIN_HZ = 110;
export const WAVE_MAX_HZ = 4400;

/**
 * How much of the byte range counts as silence.
 *
 * An open line is never numerically zero - there is comfort noise on it, and the
 * analyser's own floor besides. Without this cut the meter shows a permanent low
 * shimmer, which reads as "somebody is talking" during the exact pause where the
 * operator is deciding whether the customer is still there.
 */
const NOISE_FLOOR = 0.14;

/** Half a bin's inclusive span, so a band is never empty on a coarse FFT. */
export interface Band {
  from: number;
  to: number;
}

/**
 * Logarithmic band edges, one per bar.
 *
 * Logarithmic rather than linear because pitch is: linear bands put four fifths
 * of the bars above 2 kHz, where a voice has almost nothing, and squeeze the
 * whole of speech into the first handful. Every band is at least one bin wide -
 * on a coarse FFT the low bands would otherwise collapse onto each other and
 * draw as a flat step.
 */
export function buildBands(bars: number, binCount: number, sampleRate: number): Band[] {
  const nyquist = sampleRate / 2;
  const hzPerBin = nyquist / binCount;
  const lo = Math.log(WAVE_MIN_HZ);
  const span = Math.log(WAVE_MAX_HZ) - lo;
  const bands: Band[] = [];
  let cursor = Math.max(1, Math.floor(WAVE_MIN_HZ / hzPerBin));

  for (let i = 0; i < bars; i++) {
    const edge = Math.exp(lo + (span * (i + 1)) / bars);
    const end = Math.min(binCount - 1, Math.max(cursor, Math.floor(edge / hzPerBin)));
    bands.push({ from: Math.min(cursor, end), to: end });
    cursor = Math.min(binCount - 1, end + 1);
  }
  return bands;
}

/**
 * One FFT frame to one set of 0..1 targets.
 *
 * The PEAK of a band rather than its mean: a mean over a wide high band is
 * dominated by the empty bins beside the one that actually carries the
 * consonant, so an averaged meter goes quiet exactly where speech is at its most
 * articulate. The curve at the end lifts quiet passages - the interesting range
 * on a phone call is the top third of the byte scale, and left linear the meter
 * only moves when somebody raises their voice.
 */
export function readLevels(freq: Uint8Array, bands: Band[], out: Float32Array): void {
  for (let i = 0; i < bands.length; i++) {
    const { from, to } = bands[i]!;
    let peak = 0;
    for (let bin = from; bin <= to; bin++) {
      const value = freq[bin] ?? 0;
      if (value > peak) peak = value;
    }
    const level = peak / 255;
    const cut = level <= NOISE_FLOOR ? 0 : (level - NOISE_FLOOR) / (1 - NOISE_FLOOR);
    out[i] = Math.pow(cut, 0.72);
  }
}

/**
 * Move the drawn levels toward the measured ones, fast up and slow down.
 *
 * Asymmetric on purpose, and it is the difference between a meter and a strobe.
 * A syllable is a few frames long; following it symmetrically makes the bars
 * flicker at the frame rate, and smoothing it symmetrically enough to stop that
 * also smears away the attack that makes the thing look connected to the voice.
 * So: jump to a rise, fall away from it.
 */
export function easeLevels(levels: Float32Array, targets: Float32Array, rise: number, fall: number): void {
  for (let i = 0; i < levels.length; i++) {
    const target = targets[i] ?? 0;
    const level = levels[i] ?? 0;
    levels[i] = level + (target - level) * (target > level ? rise : fall);
  }
}
